import { ReactNode } from 'react';
import { ClipboardList, X } from 'lucide-react';
import { ReunionTransaction } from '../../../hooks/useReunionMensuelleData';

export interface ExtraColumn {
  label: string;
  render: (tx: ReunionTransaction) => ReactNode;
}

export interface MetricColumn {
  label: string;
  getValue: (tx: ReunionTransaction) => number | null;
  /** Moyenne de référence (ex : la moyenne affichée sur le graphique pour cette cellule) */
  average: number | null;
  formatValue: (v: number) => string;
}

interface SalesListModalProps {
  title: string;
  transactions: ReunionTransaction[];
  onClose: () => void;
  extraColumns?: ExtraColumn[];
  metricColumn?: MetricColumn;
}

const TYPOLOGIE_BADGE: Record<string, string> = {
  Appartement: 'bg-indigo-50 text-indigo-600',
  Maison: 'bg-emerald-50 text-emerald-600',
  Immeuble: 'bg-rose-50 text-rose-600',
  Box: 'bg-amber-50 text-amber-600',
  Terrain: 'bg-sky-50 text-sky-600',
  Parking: 'bg-violet-50 text-violet-600',
};

function formatDate(d: string | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-FR');
}

function formatEuros(v: number | null): string {
  if (v === null || v === undefined) return '—';
  return `${Math.round(v).toLocaleString('fr-FR')} €`;
}

export default function SalesListModal({ title, transactions, onClose, extraColumns = [], metricColumn }: SalesListModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-5xl max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
          <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center shrink-0">
            <ClipboardList size={16} className="text-indigo-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-800">{title}</p>
            <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-2">
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">
                {transactions.length} transaction{transactions.length > 1 ? 's' : ''}
              </span>
              {metricColumn?.average != null && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 font-medium">
                  moyenne {metricColumn.formatValue(metricColumn.average)}
                </span>
              )}
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors shrink-0">
            <X size={18} />
          </button>
        </div>

        <div className="overflow-auto flex-1">
          {transactions.length === 0 ? (
            <div className="px-6 py-10 text-center text-sm text-slate-400 italic">Aucune transaction</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-50 shadow-sm">
                <tr>
                  <th className="px-4 py-2.5 text-left font-semibold text-slate-500 text-xs uppercase tracking-wide">N° bien</th>
                  <th className="px-4 py-2.5 text-left font-semibold text-slate-500 text-xs uppercase tracking-wide">Vendeur</th>
                  <th className="px-4 py-2.5 text-left font-semibold text-slate-500 text-xs uppercase tracking-wide">Acquéreur</th>
                  {extraColumns.map(col => (
                    <th key={col.label} className="px-4 py-2.5 text-left font-semibold text-slate-500 text-xs uppercase tracking-wide">{col.label}</th>
                  ))}
                  <th className="px-4 py-2.5 text-left font-semibold text-slate-500 text-xs uppercase tracking-wide">Typologie</th>
                  <th className="px-4 py-2.5 text-right font-semibold text-slate-500 text-xs uppercase tracking-wide">Prix net vendeur</th>
                  <th className="px-4 py-2.5 text-right font-semibold text-slate-500 text-xs uppercase tracking-wide">Honoraires HT</th>
                  {metricColumn && (
                    <th className="px-4 py-2.5 text-right font-semibold text-amber-600 text-xs uppercase tracking-wide bg-amber-50/60">{metricColumn.label}</th>
                  )}
                  <th className="px-4 py-2.5 text-right font-semibold text-slate-500 text-xs uppercase tracking-wide">
                    <span className="inline-flex items-center gap-1.5 justify-end w-full"><span className="w-1.5 h-1.5 rounded-full bg-indigo-400" />Mandat</span>
                  </th>
                  <th className="px-4 py-2.5 text-right font-semibold text-slate-500 text-xs uppercase tracking-wide">
                    <span className="inline-flex items-center gap-1.5 justify-end w-full"><span className="w-1.5 h-1.5 rounded-full bg-amber-400" />Compromis</span>
                  </th>
                  <th className="px-4 py-2.5 text-right font-semibold text-slate-500 text-xs uppercase tracking-wide">
                    <span className="inline-flex items-center gap-1.5 justify-end w-full"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />Acte prévi.</span>
                  </th>
                  <th className="px-4 py-2.5 text-right font-semibold text-slate-500 text-xs uppercase tracking-wide">
                    <span className="inline-flex items-center gap-1.5 justify-end w-full"><span className="w-1.5 h-1.5 rounded-full bg-slate-400" />Acte signé</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx, i) => {
                  const metricValue = metricColumn ? metricColumn.getValue(tx) : null;
                  const isAboveAverage = metricColumn?.average != null && metricValue !== null && metricValue > metricColumn.average;
                  const typologieBadge = tx.property_type ? TYPOLOGIE_BADGE[tx.property_type] ?? 'bg-slate-100 text-slate-600' : null;
                  return (
                    <tr key={tx.id} className={`border-t border-slate-100 hover:bg-slate-50/80 transition-colors ${i % 2 === 1 ? 'bg-slate-50/30' : ''}`}>
                      <td className="px-4 py-2.5 text-slate-600 font-medium">{tx.property_number || '—'}</td>
                      <td className="px-4 py-2.5 text-slate-600">{tx.seller || '—'}</td>
                      <td className="px-4 py-2.5 text-slate-600">{tx.buyer || '—'}</td>
                      {extraColumns.map(col => (
                        <td key={col.label} className="px-4 py-2.5 text-slate-600">{col.render(tx)}</td>
                      ))}
                      <td className="px-4 py-2.5">
                        {tx.property_type ? (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${typologieBadge}`}>
                            {tx.property_type}
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-right text-slate-700 font-medium">{formatEuros(tx.sale_price)}</td>
                      <td className="px-4 py-2.5 text-right text-slate-700 font-medium">{formatEuros(tx.fees_without_tax)}</td>
                      {metricColumn && (
                        <td className="px-4 py-2.5 text-right bg-amber-50/30">
                          {metricValue === null ? (
                            <span className="text-slate-400">—</span>
                          ) : (
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${isAboveAverage ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-600'}`}>
                              {metricColumn.formatValue(metricValue)}
                            </span>
                          )}
                        </td>
                      )}
                      <td className="px-4 py-2.5 text-right text-slate-500">{formatDate(tx.mandate_date)}</td>
                      <td className="px-4 py-2.5 text-right text-slate-500">{formatDate(tx.compromise_date)}</td>
                      <td className="px-4 py-2.5 text-right text-slate-500">{formatDate(tx.expected_acte_date)}</td>
                      <td className="px-4 py-2.5 text-right text-slate-500">{formatDate(tx.acte_date)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
